import siteConfig from "site-config";
import { Observable } from "rxjs";
import { ajax } from "lib/observable";
import { formatMessage } from "lib/globalize-util";
import "rxjs/add/operator/map";
import { APIError } from "lib/errors";
import { COLLECTIONS } from "constants/action-types";
import { add as snackbarAdd } from "actions/snackbars";
import { removePermissionError, removePermissionSuccess } from "actions/collections";
import { CollectionPermissions as CollectionPermissionsSchema } from "schemas/collections";
import { UsersSchema } from "schemas/user";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import { updateEntities } from "redux-query";
import { List } from "immutable";
import querystring from "querystring";
import { push } from "react-router-redux";
import { forkJoin } from "rxjs/observable/forkJoin";
import * as collectionsActions from "actions/collections";
import me from "models/me";
import { DRIVE_LINK } from "containers/folders/utils/constants";

const parseResponse = ({ response }: any) => {
    return response.data[0];
};

export const fetchCollectionPermissionsEpic = (action$, store) => {
    let collectionPermissions;
    let collectionId;
    return action$
        .ofType(COLLECTIONS.COLLECTION.PERMISSIONS.FETCH.START)
        .map((action) => action.payload)
        .concatMap(({ itemId, merchantId, collectionId: _collectionId }) => {
            collectionId = _collectionId;
            return ajax(`${siteConfig.goAPI}/v1/merchants/${merchantId}/space/items/${itemId}/permissions`)
                .map(({ response }) => {
                    return response;
                })
                .catch((error) => {
                    return Observable.of({
                        error: true,
                        payload: new APIError(error.xhr.response.error, error.xhr.status, error.xhr.statusText),
                        type: COLLECTIONS.COLLECTION.PERMISSIONS.FETCH.COMPLETED,
                    });
                });
            // return Observable.of({
            //     data: [
            //         {
            //             collection_id: id,
            //             identity: me._id,
            //             identity_type: "USER",
            //             role: "OWNER",
            //             granted_at_ms: "1601001102862",
            //             granted_by_user_id: "5e34c88a7592590b615ebfcb",
            //             identity_brand_id: "",
            //             identity_company_id: "",
            //         },
            //     ],
            // });
        })
        .do((response) => {
            if (response.error) {
                throw response.payload;
            }
        })
        .flatMap((response) => {
            collectionPermissions = response.permissions;
            const userIds = collectionPermissions
                .map((permission) => permission?.granted_to?.user_id)
                .filter((uid) => uid);
            return fetchUsers(userIds)(action$, store);
        })
        .map(({ response }) => {
            collectionPermissions = collectionPermissions.map((permission) => {
                const userData = response.find((user) => user._id === permission?.granted_to?.user_id);
                if (userData) {
                    permission.email = userData.email;
                    permission.name = userData.name;
                }
                permission.collection_id = collectionId;

                return permission;
            });
            const formattedResponse = {
                permissions: collectionPermissions,
            };
            const records = getRecordsFromSchema(CollectionPermissionsSchema);
            const data = normalizeData(formattedResponse, CollectionPermissionsSchema, records);
            return { entities: data.entities, result: data.result };
        })
        .mergeMap(({ entities, result }: any) => {
            return [
                updateEntities({
                    collectionPermissions: (permissions = {}) => {
                        return {
                            ...permissions,
                            ...entities.collectionPermissions,
                        };
                    },
                }),
                {
                    payload: result,
                    type: COLLECTIONS.COLLECTION.PERMISSIONS.FETCH.COMPLETED,
                },
            ];
        })
        .catch((error) => {
            return Observable.merge(
                Observable.of({
                    error: true,
                    payload: error,
                    type: COLLECTIONS.COLLECTION.PERMISSIONS.FETCH.COMPLETED,
                })
            );
        });
};
// We don't know what the actual data source will be for this, since the users could be a hardcoded list'
// Pointing to the nuorder users for now
export const fetchContactsEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.PERMISSIONS.CONTACTS.FETCH.START)
        .map(({ payload }) => payload)
        .switchMap(({ search, merchant_id, brandId }) => {
            let query = "";
            if (search) {
                query = `?${querystring.stringify({ __search: search, merchant_id, brandId })}`;
            }
            return ajax(`${siteConfig.api}/collection/users${query}`)
                .map(({ response }) => {
                    if (!response) {
                        return { result: [] };
                    } else {
                        response = response.filter((user) => {
                            const isFullMatchForNuOrderUser =
                                user.email === search.trim().toLowerCase() && user.account_type === "nuorder";

                            return isFullMatchForNuOrderUser || user.account_type !== "nuorder";
                        });
                        const records = getRecordsFromSchema(UsersSchema);
                        const data = normalizeData(response, UsersSchema, records);
                        return {
                            entities: data.entities,
                            result: data.result,
                        };
                    }
                })
                .mergeMap(({ entities, result }: any) => [
                    updateEntities({
                        user: (user = {}) => {
                            return {
                                ...user,
                                ...entities.user,
                            };
                        },
                    }),
                    {
                        payload: result,
                        type: COLLECTIONS.COLLECTION.PERMISSIONS.CONTACTS.FETCH.COMPLETED,
                    },
                ])
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            payload: new Error(error),
                            error: true,
                            type: COLLECTIONS.COLLECTION.PERMISSIONS.CONTACTS.FETCH.COMPLETED,
                        }),
                        Observable.of(snackbarAdd({ message: formatMessage("There was an error fetching users") }))
                    )
                );
        });

const addPermission = ({ collectionId, userId, userType = "USER", permission = "VIEWER", merchantId }) => {
    return ajax({
        body: {
            collection_id: collectionId,
            identity: userId,
            identity_type: userType,
            role: permission,
            merchant_id: merchantId,
        },
        method: "POST",
        url: `${siteConfig.goAPI}/v1/collections/${collectionId}/permissions`,
    }).map(parseResponse);
};

const addPermissions = ({ collectionId, userIds, userType = "USER", permission = "VIEWER" }) => {
    return (action, store) => {
        const { merchantId } = store.getState().collection;
        const queries = userIds.map((id) => {
            return addPermission({ collectionId, userId: id, userType, permission, merchantId });
        });

        return queries;
    };
};

const deletePermission = (collectionId, { identity }) =>
    ajax({
        method: "DELETE",
        url: `${siteConfig.goAPI}/v1/collections/${collectionId}/permissions/${identity}`,
    })
        .map((data) => data)
        .catch(() => Observable.of(null));

const deletePermissions = (collectionId, users) => users.map((user) => deletePermission(collectionId, user));

const fetchUsers = (userIds) => {
    return (action, store) => {
        const query = {
            _id: userIds,
            __limit: 1000,
        };
        return ajax({
            method: "GET",
            url: `${siteConfig.api}/collection/users?${querystring.stringify(query)}`,
        });
    };
};

export const addPermissionsEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.PERMISSIONS.CREATE.START)
        .map((action) => {
            return action.payload;
        })
        .mergeMap(({ collectionId, userIds, permission = "VIEWER" }) => {
            const queries = addPermissions({ collectionId, userIds, permission })(action$, store);
            let updatedUsers;
            return Observable.forkJoin(queries)
                .flatMap((data) => {
                    const { entities } = store.getState();
                    updatedUsers = entities.collectionPermissions[collectionId].users.concat(data);
                    return fetchUsers(userIds)(action$, store);
                })
                .map((response) => {
                    return response;
                })
                .flatMap(({ response }) => {
                    updatedUsers = updatedUsers.toJS();
                    updatedUsers = updatedUsers.map((userPermission) => {
                        const userData = response.find((user) => user._id === userPermission.identity);
                        if (userData) {
                            userPermission.email = userData.email;
                            userPermission.name = userData.name;
                            userPermission.granted_at_ms = userPermission.granted_at_ms
                                ? userPermission.granted_at_ms.valueOf().toString()
                                : userPermission.granted_at_ms;
                        }
                        return userPermission;
                    });
                    const formattedResponse = {
                        permissions: updatedUsers,
                    };

                    const records = getRecordsFromSchema(CollectionPermissionsSchema);
                    const data = normalizeData(formattedResponse, CollectionPermissionsSchema, records);
                    return [
                        updateEntities({
                            collectionPermissions: (collectionPermission = {}) => {
                                return {
                                    ...collectionPermission,
                                    ...data.entities.collectionPermissions,
                                };
                            },
                        }),
                        snackbarAdd({ message: formatMessage("Assortment shared with user(s)") }),
                        {
                            payload: [collectionId],
                            type: COLLECTIONS.COLLECTION.PERMISSIONS.CREATE.COMPLETED,
                        },
                        {
                            type: COLLECTIONS.COLLECTION.PERMISSIONS.CONTACTS.SET,
                            payload: List(),
                        },
                    ];
                });
        })
        .catch((error) => {
            let errorMessage = formatMessage("Oops! Something went wrong. Please refresh and try again.");
            const actions = [];
            if (error.status === 401 || error.status === 403) {
                errorMessage = formatMessage("You do not have permission to share this assortment");
            } else if (error.status === 404) {
                errorMessage = formatMessage("This assortment is no longer available");
                actions.push(Observable.of(push(DRIVE_LINK)));
            }

            return Observable.merge(
                ...actions,
                Observable.of({
                    error: true,
                    payload: new Error(errorMessage),
                    type: COLLECTIONS.COLLECTION.PERMISSIONS.CREATE.COMPLETED,
                }),
                Observable.of(snackbarAdd({ message: errorMessage }))
            );
        });
};

export const updatePermissionEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.PERMISSIONS.UPDATE.START)
        .map(({ payload }) => payload)
        .mergeMap((permission) => {
            const { collectionId, identity, role } = permission;
            delete permission.granted_at_ms;
            return ajax({
                url: `${siteConfig.goAPI}/v1/collections/${collectionId}/permissions/${identity}`,
                method: "PUT",
                body: {
                    collection_id: collectionId,
                    identity,
                    permissions: [permission],
                },
            })
                .map(({ response }) => {
                    const { entities } = store.getState();
                    const updatedUser = response.data[0];
                    const existingUsers = entities.collectionPermissions[collectionId].users.toJS();
                    const updatedUserSet = existingUsers.map((user) => {
                        if (user.identity === updatedUser.identity) {
                            updatedUser.email = user.email;
                            updatedUser.name = user.name;
                            return updatedUser;
                        } else {
                            user.granted_at_ms = user.granted_at_ms.valueOf().toString();
                            return user;
                        }
                    });
                    const records = getRecordsFromSchema(CollectionPermissionsSchema);
                    const data = normalizeData({ permissions: updatedUserSet }, CollectionPermissionsSchema, records);
                    return data;
                })
                .mergeMap(({ entities }) => {
                    const successActions = [
                        updateEntities({
                            collectionPermissions: (collectionPermission = {}) => {
                                return {
                                    ...collectionPermission,
                                    ...entities.collectionPermissions,
                                };
                            },
                        }),
                    ];

                    return successActions;
                })
                .catch((error) => {
                    const actions = [];
                    let errorMessage = formatMessage("Oops! Something went wrong. Please refresh and try again.");
                    if (error.status === 401 || error.status === 403) {
                        errorMessage = formatMessage("You do not have permission to share this assortment");
                    } else if (error.status === 404) {
                        errorMessage = formatMessage("This assortment is no longer available");
                        actions.push(Observable.of(push(DRIVE_LINK)));
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: new Error(errorMessage),
                            type: COLLECTIONS.COLLECTION.PERMISSIONS.UPDATE.COMPLETED,
                        }),
                        Observable.of(snackbarAdd({ message: errorMessage }))
                    );
                });
        });

export const deletePermissionEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.PERMISSIONS.DELETE.START)
        .map(({ payload }) => payload)
        .mergeMap(({ users, collectionId }) =>
            forkJoin(deletePermissions(collectionId, users))
                .map((responses) => {
                    const { entities } = store.getState();
                    const updatedUsers = entities.collectionPermissions[collectionId].users.filter(
                        (collectionPermissionsUser) =>
                            !users.find(
                                (user, index) =>
                                    responses[index] && user.identity === collectionPermissionsUser.identity
                            )
                    );
                    const records = getRecordsFromSchema(CollectionPermissionsSchema);
                    const data = normalizeData(
                        { permissions: updatedUsers.toJS() },
                        CollectionPermissionsSchema,
                        records
                    );
                    return { responses, data };
                })
                .mergeMap(({ responses, data }) => {
                    const { routing } = store.getState();
                    let observables = [
                        updateEntities({
                            collectionPermissions: (collectionPermission = {}) => ({
                                ...collectionPermission,
                                ...data.entities.collectionPermissions,
                            }),
                        }),
                    ];
                    if (responses.some((response) => !response)) {
                        observables = observables.concat(
                            removePermissionError(),
                            snackbarAdd({
                                message: formatMessage("There was an error removing the user(s) permission."),
                            })
                        );
                    } else {
                        observables = observables.concat(
                            removePermissionSuccess(),
                            collectionsActions.deleteCollectionSuccess(collectionId)
                        );
                        if (users.length > 1 || users[0].identity !== me._id) {
                            observables.push(snackbarAdd({ message: formatMessage("User(s) permissions removed.") }));
                        }
                    }
                    if (
                        users.length === 1 &&
                        users[0].identity === me._id &&
                        routing.locationBeforeTransitions.pathname.includes("/assortment/")
                    ) {
                        observables.push(push(DRIVE_LINK));
                    }
                    return observables;
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of(removePermissionError(error)),
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error removing the user(s) permission."),
                            })
                        )
                    )
                )
        );
